import React, { useMemo, useState } from "react";
import { filter, get, map, orderBy, reduce, toNumber, toUpper } from "lodash";
import { useTranslation } from "react-i18next";
import { Table, TableProps } from "@/shared/widgets/Table";
import { IconButton } from "@/shared/ui/IconButton";
import { DropdownMenu } from "@/shared/widgets/DropdownMenu";
import { Icon, IconName } from "@/shared/ui/Icon";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import { useSortAndPagination } from "@/shared/utils/hooks/useSortAndPagination";
import { useDeleteProject } from "../../hooks/useDeleteProject";
import { useScorecardList } from "@/features/scorecards/hooks/useScorecardsList";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalFileName } from "@/shared/modals";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import { parseCustomColumn, searchCustomColumns } from "./CustomColumns";
import { Row } from "@/shared/ui/Blocks";
import { InputField } from "@/shared/ui/InputField";
import { Project } from "@/features/projects/graphql/generated";
import { getResourceSurveyType } from "@/shared/utils/selectors/getSurveyTypes";
import { AssessmentSurveyGroup } from "@/shared/constants/assessmentSurveys";

interface ProjectsTableProps<T extends {}>
  extends Omit<TableProps<T>, "columns"> {
  data: T[];
  readOnly?: boolean;
}

function ProjectOptions({ project }: { project: any }) {
  const { t } = useTranslation();
  const navigate = useOrgNavigate();

  const actionWrapper = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    action();
  };

  const { handleConfirmDeleteProject } = useDeleteProject();

  const options = [
    {
      label: t("projects.edit_project"),
      onClick: actionWrapper(() =>
        navigate(`/projects/${project.id}/settings`)
      ),
      icon: "Edit" as IconName,
    },
    // { // Commented out until we think of a solution to invite collaborators to projects
    //   label: t("projects.invite_collaborators"),
    //   onClick: actionWrapper(() =>
    //     navigate(`/projects/${project.id}/settings#collaborators`)
    //   ),
    //   icon: "Send" as IconName,
    // },
    {
      label: t("projects.archive_project"),
      onClick: actionWrapper(() => {
        handleConfirmDeleteProject(project.id, {});
      }),
      icon: "Delete" as IconName,
    },
  ];

  return (
    <DropdownMenu
      items={options}
      sideOffset={-24}
      trigger={
        <IconButton icon="KebabVertical" color="black" variant="ghost" />
      }
    />
  );
}

function ProjectsTable<T extends Project>(props: ProjectsTableProps<T>) {
  const { data = [], readOnly, ...rest } = props;

  const { getAccountConfigValue } = useAccount();
  const [searchValue, setSearchValue] = useState("");

  const projectCustomColumns = getAccountConfigValue(
    "feature_config.projects.custom_columns"
  );

  const newSurveysEnabled = getAccountConfigValue("surveys") as boolean;

  const { scorecards } = useScorecardList({
    source: "all",
    ids: data.map((d) => toNumber(d.scorecard_id)).filter((id) => !!id),
  });

  const {
    page,
    limit,
    sort,
    handleSortChange,
    handleLimitChange,
    handlePageChange,
  } = useSortAndPagination({
    initialSort: { key: "id", direction: "desc" },
  });

  const [openScorecardPicker] = useModal(ModalFileName.SCORECARD_PICKER);

  const { t } = useTranslation();

  const navigate = useOrgNavigate();

  const tableData: T[] = useMemo(() => {
    const aggregatedData = map(data, (d) => ({
      ...d,
      survey_type: getResourceSurveyType(d.config?.dimensions || []),
      scorecard:
        scorecards.find((s) => (s as any).id === d.scorecard_id)?.title || "",
    }));

    const sortKey = sort.key;
    const sortedData = orderBy(aggregatedData, [sortKey], [
      sort.direction || "desc",
    ] as any);

    const filteredData = filter(
      sortedData,
      (project: Project) =>
        project?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
        project?.client_name
          ?.toLowerCase()
          .includes(searchValue.toLowerCase()) ||
        searchCustomColumns(projectCustomColumns, project, searchValue)
    );

    return filteredData.slice((page - 1) * limit, page * limit);
  }, [data, sort, page, limit, searchValue, scorecards]);

  const columns = {
    name: {
      title: t("projects.project_name"),
      sortable: true,
      sortDirection: sort?.key === "name" ? sort.direction : undefined,
      className: "!whitespace-normal",
      order: 1,
      icon: "Clipboard",
      accessor: (data: any) => (
        <p className="font-semibold text-sm w-full line-clamp-3">{data.name}</p>
      ),
    },
    client_name: {
      title: "Client",
      span: 2,
      sortable: true,
      sortDirection: sort?.key === "client_name" ? sort.direction : undefined,
      className: "whitespace-normal",
      accessor: (data: any) => data?.client_name || "",
      order: 2,
      hidden: import.meta.env.VITE_GPT_FLOW_CONFIG !== "rraqa",
    },
    survey_type: {
      title: t("projects.survey_type"),
      sortable: true,
      sortDirection: sort?.key === "survey_type" ? sort.direction : undefined,
      span: 2,
      order: 3,
      className: "whitespace-normal",
      icon: "SurveyQuestion",
      accessor: (data: any) => {
        const icon =
          data.survey_type === AssessmentSurveyGroup.WISNIO
            ? "WisnioIcon"
            : "APIcon";
        const title =
          data.survey_type === AssessmentSurveyGroup.WISNIO
            ? t("projects.wisnio_surveys")
            : t("projects.ap_surveys");
        return (
          <Row className="items-center gap-2">
            <Icon icon={icon} size="sm" />
            <p className="text-xs">{toUpper(title)}</p>
          </Row>
        );
      },
      hidden:
        import.meta.env.VITE_GPT_FLOW_CONFIG === "rraqa" || !newSurveysEnabled,
    },
    scorecard: {
      title: t("projects.scorecard"),
      span: 2,
      order: 3,
      sortable: true,
      sortDirection: sort?.key === "scorecard" ? sort.direction : undefined,
      className: "whitespace-normal",
      icon: "AssessmentReport",
      accessor: (data: any) => {
        if (!data.scorecard_id) {
          return (
            <IconButton
              icon="Plus"
              variant="ghost"
              color="blue"
              className="text-blue-500 px-0"
              label="Add scorecard"
              onClick={(e) => {
                openScorecardPicker({ projectId: data.id });
                e.stopPropagation();
              }}
            />
          );
        }
        return <p className="text-sm">{data.scorecard}</p>;
      },
    },
    ...reduce(
      projectCustomColumns,
      (acc: any, column: any, key: string) => {
        acc[key] = {
          title: column.title,
          span: column.span || 1,
          order: column.order,
          accessor: (data: any) =>
            parseCustomColumn(
              column.type,
              get(data, column.data_key || key) || ""
            ),
        };
        return acc;
      },
      {}
    ),

    actions: {
      title: "",
      span: 0,
      accessor: (data: any) =>
        readOnly ? null : <ProjectOptions project={data} />,
    },
  };

  const handleRowClick = (row: T) => {
    navigate(`/projects/${row.id}`);
  };

  const handleSearchProject = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  return (
    <>
      <Row className="justify-between md:items-center flex-col md:flex-row gap-4">
        <Row className="md:w-[40%]">
          <InputField
            variant="ghost"
            size="small"
            deleteIcon
            icon="Search"
            type="text"
            name="search"
            onClearValue={() => setSearchValue("")}
            placeholder="Search projects"
            autoComplete="off"
            onChange={handleSearchProject}
            wrapperClassName="border-b-grey-300 border-b border-solid"
          />
        </Row>
        {/* <Row className="space-x-4 justify-between md:justify-normal">
          <FiltersDropdown
            filters={listFilters}
            onSelect={handleFilterChange}
          />
        </Row> */}
      </Row>
      <Table
        onRowClick={handleRowClick}
        {...rest}
        columns={columns}
        data={tableData}
        pages={Math.ceil(data.length / limit) || 1}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onSort={handleSortChange}
        limit={limit}
        currentPage={page}
      />
    </>
  );
}

export default ProjectsTable;
export { ProjectsTable };
